import { useState, type ReactNode } from 'react';
import { Link } from '../../router/Link';
import { cx } from '../../ui/cx';
import {
  Button,
  CloudIcon,
  ConfirmButton,
  Field,
  Menu,
  Notice,
  Panel,
  ScreenIcon,
  SelectMenu,
  Spinner,
  Toggle,
} from '../../ui';
import { useSettingsScreen, type SettingsBinding } from './useSettingsScreen';
import { WatchLaterPicker } from '../../playlists/WatchLaterPicker';
import {
  type LandingId,
  type SettingsTab,
  type SponsorCategoryRow,
  audioSelectValue,
  capabilityRows,
  dataControlLink,
  forceAvcOn,
  instancePreferencesLink,
  instanceSwitchWarning,
  landingOptions,
  languageOptions,
  menuSectionsMenu,
  peerTubeModeOptions,
  peerTubeSearchTargetOptions,
  probeStepRows,
  qualityOptions,
  qualitySelectValue,
  regionOptions,
  settingsTabLinks,
  speedOptions,
  sponsorApiFieldState,
  sponsorCategoryRows,
  subtitleSelectValue,
  uiLanguageRow,
  uiLanguageOptions,
  themeOptions,
  type SyncTone,
} from './model';
import { useCopy } from '../../strings/useCopy';
import layout from '../../ui/layout.module.css';
import styles from './settings.module.css';

/**
 * §8's Settings page. Renders props (R13) — every sentence arrived formatted, every list arrived
 * filtered, and every control hands its raw value straight to a setter. There is no decision in this
 * file about what a value *means*: which arm of `VideoQualityPref` a height builds, whether the forced
 * codec rides along, what "no preference" is — all of that is `model.ts`.
 *
 * The page is five topic tabs carried by the route (`/settings/<tab>`, the channel page's shape), and
 * inside a tab **every setting is one row**: title on the left with one line of description under it,
 * the control on the right, the longer story — where one exists — as the row's hover title. The two
 * deliberate exceptions to the row grammar are the instance card (an address, two buttons and a
 * five-step report is not a row) and the Data tab's clear buttons.
 */
export function SettingsView({ tab }: { readonly tab: SettingsTab }) {
  const binding = useSettingsScreen();
  const words = useCopy().text.settings;

  return (
    /*
     * `layout.page` is the frame, on the outside, exactly as the other twelve screens do it (the 3e
     * lesson: a `max-width` alone caps a width without centring it). The screen's own `.page` is the
     * stack — a tighter gap than the frame's, and `min-width: 0`.
     */
    <div className={layout.page}>
      <section className={styles.page}>
        <header className={styles.head}>
          <h1 className={styles.heading}>{words.heading}</h1>
          <p className={styles.lede}>{words.lede}</p>
        </header>

        {/* Tabs are `<Link>`s, not buttons — destinations, so Back means "the tab I was on". */}
        <nav className={styles.tabs} aria-label={words.tabsLabel}>
          {settingsTabLinks(tab).map((link) => (
            <Link
              key={link.tab}
              className={link.current ? styles.tabCurrent : styles.tab}
              href={link.href}
              ariaCurrent={link.current}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {tab === 'playback' ? <PlaybackTab binding={binding} /> : null}
        {tab === 'interface' ? <InterfaceTab binding={binding} /> : null}
        {tab === 'integrations' ? <IntegrationsTab binding={binding} /> : null}
        {tab === 'instance' ? <InstanceTab binding={binding} /> : null}
        {tab === 'data' ? <DataTab binding={binding} /> : null}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The row grammar
 * ---------------------------------------------------------------------------------------------- */

/**
 * One setting: title left, one-line description under it, control right. `detailTitle` is the longer
 * explanation as the text block's hover title — the visible line is owed exactly one line, and a
 * paragraph per row would turn the page back into the essay the tabs exist to break up.
 *
 * `storage` is the icon after the name — where the setting lives, account or browser — with its own
 * hover title carrying the words. An icon rather than a sentence per row: the where-stored story
 * used to take a clause in half the details, saying the same two things in ten wordings.
 */
function SettingRow({
  title,
  detail,
  detailId,
  detailTitle,
  storage,
  control,
}: {
  readonly title: string;
  readonly detail?: string;
  /** Set when the control points `aria-describedby` at the detail line. */
  readonly detailId?: string;
  readonly detailTitle?: string;
  readonly storage?: 'account' | 'browser';
  readonly control: ReactNode;
}) {
  const words = useCopy().text.settings.storage;
  return (
    <div className={styles.row}>
      <div className={styles.rowText} title={detailTitle}>
        <span className={styles.rowTitle}>
          {title}
          {storage === undefined ? null : (
            <span
              className={styles.storageIcon}
              role="img"
              aria-label={words[storage]}
              title={words[storage]}
            >
              {storage === 'account' ? <CloudIcon /> : <ScreenIcon />}
            </span>
          )}
        </span>
        {detail === undefined ? null : (
          <span className={styles.rowDetail} id={detailId}>
            {detail}
          </span>
        )}
      </div>
      <div className={styles.rowControl}>{control}</div>
    </div>
  );
}

const detailIdOf = (controlId: string): string => `${controlId}-rowdetail`;

/* ------------------------------------------------------------------------------------------------
 * Playback
 * ---------------------------------------------------------------------------------------------- */

function PlaybackTab({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const words = useCopy().text.settings;
  const control = words.control;

  return (
    <Panel as="section" ariaLabel={words.tab.playback} className={styles.section}>
      <div className={styles.rows}>
        <SettingRow
          title={control.quality.label}
          storage="account"
          detail={control.quality.hint}
          control={
            <SelectMenu
              label={control.quality.label}
              className={styles.rowSelect}
              value={qualitySelectValue(settings)}
              options={qualityOptions()}
              onChange={binding.setQuality}
            />
          }
        />
        <SettingRow
          title={control.speed.label}
          storage="account"
          control={
            <SelectMenu
              label={control.speed.label}
              className={styles.rowSelect}
              value={String(settings.playback.playbackRate)}
              options={speedOptions()}
              onChange={binding.setSpeed}
            />
          }
        />
        <SettingRow
          title={control.forceAvc.label}
          storage="browser"
          detail={control.forceAvc.detail}
          detailId={detailIdOf('settings-force-avc')}
          detailTitle={control.forceAvc.detailTitle}
          control={
            <Toggle
              id="settings-force-avc"
              label={control.forceAvc.label}
              hideLabel
              describedBy={detailIdOf('settings-force-avc')}
              checked={forceAvcOn(settings)}
              onChange={binding.setForceAvc}
            />
          }
        />
        <SettingRow
          title={control.autoplay.label}
          storage="account"
          detail={control.autoplay.detail}
          detailId={detailIdOf('settings-autoplay')}
          detailTitle={control.autoplay.detailTitle}
          control={
            <Toggle
              id="settings-autoplay"
              label={control.autoplay.label}
              hideLabel
              describedBy={detailIdOf('settings-autoplay')}
              checked={settings.autoplay}
              onChange={binding.setAutoplay}
            />
          }
        />
        <SettingRow
          title={control.audioLanguage.label}
          storage="browser"
          detail={control.audioLanguage.detail}
          control={
            <SelectMenu
              label={control.audioLanguage.label}
              className={styles.rowSelect}
              value={audioSelectValue(settings)}
              options={[{ id: '', label: control.audioLanguage.original }, ...languageOptions().map(toOption)]}
              onChange={binding.setAudioLanguage}
            />
          }
        />
        <SettingRow
          title={control.useStableVolume.label}
          storage="browser"
          detail={control.useStableVolume.detail}
          detailId={detailIdOf('settings-drc')}
          detailTitle={control.useStableVolume.detailTitle}
          control={
            <Toggle
              id="settings-drc"
              label={control.useStableVolume.label}
              hideLabel
              describedBy={detailIdOf('settings-drc')}
              checked={settings.useStableVolume}
              onChange={binding.setUseStableVolume}
            />
          }
        />
        <SettingRow
          title={control.subtitleLanguage.label}
          storage="browser"
          detail={control.subtitleLanguage.detail}
          control={
            <SelectMenu
              label={control.subtitleLanguage.label}
              className={styles.rowSelect}
              value={subtitleSelectValue(settings)}
              options={[{ id: '', label: control.subtitleLanguage.off }, ...languageOptions().map(toOption)]}
              onChange={binding.setSubtitleLanguage}
            />
          }
        />
        <SettingRow
          title={control.youtubeEmbed.label}
          storage="browser"
          detail={control.youtubeEmbed.detail}
          detailId={detailIdOf('settings-youtube-embed')}
          detailTitle={control.youtubeEmbed.detailTitle}
          control={
            <Toggle
              id="settings-youtube-embed"
              label={control.youtubeEmbed.label}
              hideLabel
              describedBy={detailIdOf('settings-youtube-embed')}
              checked={settings.youtubeEmbed.enabled}
              onChange={binding.setYoutubeEmbedEnabled}
            />
          }
        />
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Interface
 * ---------------------------------------------------------------------------------------------- */

function InterfaceTab({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const copy = useCopy();
  const words = copy.text.settings;
  const control = words.control;
  const language = uiLanguageRow(binding.locale);
  const [menuOpen, setMenuOpen] = useState(false);
  const [watchLaterOpen, setWatchLaterOpen] = useState(false);

  return (
    <Panel as="section" ariaLabel={words.tab.interface} className={styles.section}>
      <div className={styles.rows}>
        <SettingRow
          title={control.theme.label}
          storage="account"
          detail={control.theme.hint}
          control={
            <SelectMenu
              label={control.theme.label}
              className={styles.rowSelect}
              value={settings.theme}
              options={themeOptions().map((option) => ({ id: option.id, label: option.label }))}
              onChange={binding.setTheme}
            />
          }
        />
        {/* 4e. The list is `UI_LOCALES` plus "follow the system"; the visible line names the language
            that *resolved* and why, and the full story (formatting locale, account note) is the hover
            title. */}
        <SettingRow
          title={control.language.label}
          storage="account"
          detail={language.detail}
          detailTitle={language.title}
          control={
            <SelectMenu
              label={control.language.label}
              className={styles.rowSelect}
              value={settings.uiLocale ?? ''}
              options={uiLanguageOptions()}
              onChange={binding.setUiLanguage}
            />
          }
        />
        {/* The four show/hide choices as one multi-select control, so the whole thing is a row.
            Popular's entry is disabled with the reason while the instance has the endpoint off.
            The `select` face and the shared width keep it reading as one of the row's selects —
            only the dropdown is custom. */}
        <SettingRow
          title={control.menu.label}
          storage="browser"
          detail={control.menu.detail}
          detailTitle={control.menu.detailTitle}
          control={
            <Menu
              menu={menuSectionsMenu(settings, binding.popularDisabled)}
              open={menuOpen}
              onOpenChange={setMenuOpen}
              onSelect={(id) => binding.toggleMenuItem(id as LandingId)}
              multiSelect
              variant="select"
              className={styles.rowSelect}
              showTriggerLabel={false}
            />
          }
        />
        {/* Directly under Menu sections: the two are one subject — what the menu offers, and
            where the one destination it no longer offers went. A toggle, because the section is either
            there or it is not, and browser-local like the visibility choices above it. */}
        <SettingRow
          title={control.trendingOnPopular.label}
          storage="browser"
          detail={control.trendingOnPopular.detail}
          detailTitle={control.trendingOnPopular.detailTitle}
          detailId={detailIdOf('settings-trending-on-popular')}
          control={
            <Toggle
              id="settings-trending-on-popular"
              label={control.trendingOnPopular.label}
              hideLabel
              describedBy={detailIdOf('settings-trending-on-popular')}
              checked={settings.trendingOnPopular}
              onChange={binding.setTrendingOnPopular}
            />
          }
        />
        <SettingRow
          title={control.landing.label}
          storage="account"
          detail={control.landing.hint}
          control={
            <SelectMenu
              label={control.landing.label}
              className={styles.rowSelect}
              value={settings.landing}
              options={landingOptions().map((option) => ({ id: option.id, label: option.label }))}
              onChange={binding.setLanding}
            />
          }
        />
        <SettingRow
          title={control.region.label}
          storage="account"
          detail={control.region.hint}
          control={
            <SelectMenu
              label={control.region.label}
              className={styles.rowSelect}
              value={settings.contentRegion ?? ''}
              options={regionOptions(copy.format.regionName, copy.format.compareText)}
              onChange={binding.setRegion}
            />
          }
        />
        {/* The grid hover previews (2026-08-02) — a toggle, because the feature is a motion
            choice rather than a value: some readers find flipping cards noisy. */}
        <SettingRow
          title={control.gridPreviews.label}
          storage="browser"
          detail={control.gridPreviews.detail}
          detailTitle={control.gridPreviews.detailTitle}
          detailId={detailIdOf('settings-grid-previews')}
          control={
            <Toggle
              id="settings-grid-previews"
              label={control.gridPreviews.label}
              hideLabel
              describedBy={detailIdOf('settings-grid-previews')}
              checked={settings.gridPreviews}
              onChange={binding.setGridPreviews}
            />
          }
        />
        {/* The corner player — a toggle beside Video previews for that row's reason: both are
            about motion the app starts on its own, and off is what this app did before the feature. */}
        <SettingRow
          title={control.miniPlayer.label}
          storage="browser"
          detail={control.miniPlayer.detail}
          detailTitle={control.miniPlayer.detailTitle}
          detailId={detailIdOf('settings-mini-player')}
          control={
            <Toggle
              id="settings-mini-player"
              label={control.miniPlayer.label}
              hideLabel
              describedBy={detailIdOf('settings-mini-player')}
              checked={settings.miniPlayer}
              onChange={binding.setMiniPlayer}
            />
          }
        />
        {/* The Watch later home: the control's face is the appointed list's name, and pressing
            it opens the same picker every Watch later button opens on its first press. */}
        <SettingRow
          title={control.watchLater.label}
          storage="browser"
          detail={control.watchLater.hint}
          detailTitle={control.watchLater.hintTitle}
          control={
            /* Wrapped like the selects wrap their `Menu`: `.rowSelect`'s 14em must resolve against
               the page's type, not the button's own smaller face — bare, the same em measured 42 px
               narrower than the selects above it. */
            <div className={styles.rowSelect}>
              <Button
                className={styles.rowButton}
                label={control.watchLater.label}
                onClick={() => setWatchLaterOpen(true)}
              >
                {settings.watchLater?.title ?? control.watchLater.none}
              </Button>
            </div>
          }
        />
        <WatchLaterPicker
          open={watchLaterOpen}
          current={settings.watchLater}
          signedIn={binding.data.signedIn}
          onClose={() => setWatchLaterOpen(false)}
          onPicked={() => setWatchLaterOpen(false)}
        />
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Integrations — SponsorBlock and DeArrow
 * ---------------------------------------------------------------------------------------------- */

/**
 * Two cards, one integration each (SponsorBlock 2026-08-01, DeArrow 2026-08-02) — the same
 * grammar: while an integration is off its enable row is the whole card, because dimmed knobs for
 * a feature that does nothing are noise, and the one switch that matters is easier to find alone.
 */
function IntegrationsTab({ binding }: { readonly binding: SettingsBinding }) {
  return (
    <>
      <SponsorBlockCard binding={binding} />
      <DeArrowCard binding={binding} />
      <ReturnYoutubeDislikeCard binding={binding} />
      <PeerTubeCard binding={binding} />
    </>
  );
}

/**
 * The service rows in the page's own grammar, then — under its own heading — the category grid,
 * the tab's deliberate exception to it: eight rows of a swatch and a menu, where a title-and-detail
 * per control would say the same things eight times.
 */
function SponsorBlockCard({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const words = useCopy().text.settings.sponsorBlock;
  const field = sponsorApiFieldState(binding.sponsorApiText);

  return (
    <Panel as="section" ariaLabel={words.title} className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.subTitle}>{words.title}</h2>
        <p className={styles.sectionLede}>{words.lede}</p>
      </div>

      <div className={styles.rows}>
        <SettingRow
          title={words.enable.label}
          storage="browser"
          detail={words.enable.detail}
          detailId={detailIdOf('settings-sb-enabled')}
          control={
            <Toggle
              id="settings-sb-enabled"
              label={words.enable.label}
              hideLabel
              describedBy={detailIdOf('settings-sb-enabled')}
              checked={settings.sponsorBlock.enabled}
              onChange={binding.setSponsorEnabled}
            />
          }
        />
        {!settings.sponsorBlock.enabled ? null : (
          <SettingRow
            title={words.address.label}
            storage="browser"
            detail={words.address.detail}
            control={
              <Field
                id="settings-sb-address"
                label={words.address.label}
                hideLabel
                value={binding.sponsorApiText}
                onChange={binding.setSponsorApiText}
                placeholder="https://sponsor.ajay.app"
                problem={field.problem}
                className={styles.sponsorField}
                autoComplete="url"
              />
            }
          />
        )}
      </div>

      {!settings.sponsorBlock.enabled ? null : (
        <>
          <div className={cx(styles.cardHead, styles.categoriesHead)}>
            <h3 className={styles.subTitle}>{words.categories.title}</h3>
            <p className={styles.sectionLede}>{words.categories.lede}</p>
          </div>

          <div className={styles.rows}>
            {sponsorCategoryRows(settings).map((row) => (
              <SponsorCategoryRowView key={row.id} row={row} binding={binding} />
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * DeArrow (2026-08-02): SponsorBlock's card without the categories — an enable switch, the
 * thumbnails switch (its own row because the thumbnail fetch carries the plain video id, a
 * different privacy statement from the hashed title lookup), and an address row per service.
 */
function DeArrowCard({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const words = useCopy().text.settings.deArrow;
  const apiField = sponsorApiFieldState(binding.deArrowApiText);
  const thumbField = sponsorApiFieldState(binding.deArrowThumbApiText);

  return (
    <Panel as="section" ariaLabel={words.title} className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.subTitle}>{words.title}</h2>
        <p className={styles.sectionLede}>{words.lede}</p>
      </div>

      <div className={styles.rows}>
        <SettingRow
          title={words.enable.label}
          storage="browser"
          detail={words.enable.detail}
          detailId={detailIdOf('settings-da-enabled')}
          control={
            <Toggle
              id="settings-da-enabled"
              label={words.enable.label}
              hideLabel
              describedBy={detailIdOf('settings-da-enabled')}
              checked={settings.deArrow.enabled}
              onChange={binding.setDeArrowEnabled}
            />
          }
        />
        {!settings.deArrow.enabled ? null : (
          <>
            <SettingRow
              title={words.thumbnails.label}
              storage="browser"
              detail={words.thumbnails.detail}
              detailId={detailIdOf('settings-da-thumbnails')}
              control={
                <Toggle
                  id="settings-da-thumbnails"
                  label={words.thumbnails.label}
                  hideLabel
                  describedBy={detailIdOf('settings-da-thumbnails')}
                  checked={settings.deArrow.replaceThumbnails}
                  onChange={binding.setDeArrowReplaceThumbnails}
                />
              }
            />
            <SettingRow
              title={words.address.label}
              storage="browser"
              detail={words.address.detail}
              control={
                <Field
                  id="settings-da-address"
                  label={words.address.label}
                  hideLabel
                  value={binding.deArrowApiText}
                  onChange={binding.setDeArrowApiText}
                  placeholder="https://sponsor.ajay.app"
                  problem={apiField.problem}
                  className={styles.sponsorField}
                  autoComplete="url"
                />
              }
            />
            {!settings.deArrow.replaceThumbnails ? null : (
              <SettingRow
                title={words.thumbnailAddress.label}
                storage="browser"
                detail={words.thumbnailAddress.detail}
                control={
                  <Field
                    id="settings-da-thumb-address"
                    label={words.thumbnailAddress.label}
                    hideLabel
                    value={binding.deArrowThumbApiText}
                    onChange={binding.setDeArrowThumbApiText}
                    placeholder="https://dearrow-thumb.ajay.app"
                    problem={thumbField.problem}
                    className={styles.sponsorField}
                    autoComplete="url"
                  />
                }
              />
            )}
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * Return YouTube Dislike: SponsorBlock's simplest shape, one enable switch and one address row —
 * no categories, no second toggle.
 */
function ReturnYoutubeDislikeCard({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const words = useCopy().text.settings.returnYoutubeDislike;
  const field = sponsorApiFieldState(binding.returnYoutubeDislikeApiText);

  return (
    <Panel as="section" ariaLabel={words.title} className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.subTitle}>{words.title}</h2>
        <p className={styles.sectionLede}>{words.lede}</p>
      </div>

      <div className={styles.rows}>
        <SettingRow
          title={words.enable.label}
          storage="browser"
          detail={words.enable.detail}
          detailId={detailIdOf('settings-ryd-enabled')}
          control={
            <Toggle
              id="settings-ryd-enabled"
              label={words.enable.label}
              hideLabel
              describedBy={detailIdOf('settings-ryd-enabled')}
              checked={settings.returnYoutubeDislike.enabled}
              onChange={binding.setReturnYoutubeDislikeEnabled}
            />
          }
        />
        {!settings.returnYoutubeDislike.enabled ? null : (
          <SettingRow
            title={words.address.label}
            storage="browser"
            detail={words.address.detail}
            control={
              <Field
                id="settings-ryd-address"
                label={words.address.label}
                hideLabel
                value={binding.returnYoutubeDislikeApiText}
                onChange={binding.setReturnYoutubeDislikeApiText}
                placeholder="https://returnyoutubedislikeapi.com"
                problem={field.problem}
                className={styles.sponsorField}
                autoComplete="url"
              />
            }
          />
        )}
      </div>
    </Panel>
  );
}

/**
 * PeerTube: SponsorBlock's address-field grammar plus two more rows, since unlike the other two
 * integrations this one offers a choice — which videos the search instance is asked about, and
 * which player a matched video plays in.
 */
function PeerTubeCard({ binding }: { readonly binding: SettingsBinding }) {
  const settings = binding.state.settings;
  const words = useCopy().text.settings.peerTube;
  const field = sponsorApiFieldState(binding.peerTubeApiText);

  return (
    <Panel as="section" ariaLabel={words.title} className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.subTitle}>{words.title}</h2>
        <p className={styles.sectionLede}>{words.lede}</p>
      </div>

      <div className={styles.rows}>
        <SettingRow
          title={words.enable.label}
          storage="browser"
          detail={words.enable.detail}
          detailId={detailIdOf('settings-peertube-enabled')}
          control={
            <Toggle
              id="settings-peertube-enabled"
              label={words.enable.label}
              hideLabel
              describedBy={detailIdOf('settings-peertube-enabled')}
              checked={settings.peerTube.enabled}
              onChange={binding.setPeerTubeEnabled}
            />
          }
        />
        {!settings.peerTube.enabled ? null : (
          <>
            <SettingRow
              title={words.address.label}
              storage="browser"
              detail={words.address.detail}
              control={
                <Field
                  id="settings-peertube-address"
                  label={words.address.label}
                  hideLabel
                  value={binding.peerTubeApiText}
                  onChange={binding.setPeerTubeApiText}
                  placeholder="https://sepiasearch.org"
                  problem={field.problem}
                  className={styles.sponsorField}
                  autoComplete="url"
                />
              }
            />
            <SettingRow
              title={words.searchTarget.label}
              storage="browser"
              detail={words.searchTarget.detail}
              control={
                <SelectMenu
                  label={words.searchTarget.label}
                  className={styles.rowSelect}
                  value={settings.peerTube.searchTarget}
                  options={peerTubeSearchTargetOptions()}
                  onChange={binding.setPeerTubeSearchTarget}
                />
              }
            />
            <SettingRow
              title={words.mode.label}
              storage="browser"
              detail={words.mode.detail}
              control={
                <SelectMenu
                  label={words.mode.label}
                  className={styles.rowSelect}
                  value={settings.peerTube.mode}
                  options={peerTubeModeOptions()}
                  onChange={binding.setPeerTubeMode}
                />
              }
            />
          </>
        )}
      </div>
    </Panel>
  );
}

/** Its own component for the open state's sake — each category's menu opens and closes alone. */
function SponsorCategoryRowView({
  row,
  binding,
}: {
  readonly row: SponsorCategoryRow;
  readonly binding: SettingsBinding;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <span className={styles.rowTitle}>{row.label}</span>
      </div>
      <div className={styles.categoryControls}>
        <input
          type="color"
          className={styles.colorInput}
          value={row.color}
          onChange={(event) => binding.setSponsorCategoryColor(row.id, event.target.value)}
          aria-label={row.colorLabel}
          title={row.colorLabel}
        />
        {/* Stays open across presses, the menu-sections row's manner — a reader setting a category
            usually adjusts the marks right after the behaviour. */}
        <Menu
          menu={row.menu}
          open={open}
          onOpenChange={setOpen}
          onSelect={(itemId) => binding.toggleSponsorCategoryItem(row.id, itemId)}
          variant="select"
          className={styles.rowSelect}
          showTriggerLabel={false}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Instance
 * ---------------------------------------------------------------------------------------------- */

const OUTCOME_CLASS: Readonly<Record<string, string | undefined>> = {
  ok: styles.dotOk,
  warning: styles.dotWarning,
  failed: styles.dotFailed,
  skipped: styles.dotSkipped,
};

const STATE_CLASS: Readonly<Record<string, string | undefined>> = {
  serves: styles.stateServes,
  disabled: styles.stateDisabled,
  unknown: styles.stateUnknown,
};

function InstanceTab({ binding }: { readonly binding: SettingsBinding }) {
  const { instance } = binding.state;
  const report = binding.report;
  const words = useCopy().text.settings;
  const preferencesLink = instancePreferencesLink(instance.url);

  return (
    <>
      {/* The page's deliberate exception to the row grammar: an address, two buttons and a five-step
          report is a card, not a row. */}
      <Panel as="section" ariaLabel={words.tab.instance} className={styles.section}>
        <p className={styles.sectionLede}>
          {instance.host === null ? (
            words.instance.resolving
          ) : (
            <>
              {words.instance.connectedTo} <strong>{instance.host}</strong>
              {instance.sourceText === null ? null : <>, {instance.sourceText}</>}.
            </>
          )}
        </p>

        <div className={styles.instanceRow}>
          <Field
            id="settings-instance"
            label={words.instance.addressLabel}
            value={instance.field.value}
            onChange={binding.setInstanceText}
            onSubmit={binding.check}
            placeholder={words.instance.addressPlaceholder}
            problem={instance.field.problem}
            className={styles.instanceField}
            autoComplete="url"
          />
          <div className={styles.instanceActions}>
            <Button
              onClick={binding.check}
              disabled={instance.field.normalized === null || binding.probing}
            >
              {binding.probing ? words.instance.checking : words.instance.check}
            </Button>
            <Button variant="primary" onClick={binding.switchInstance} disabled={!binding.canSwitch}>
              {words.instance.switch}
            </Button>
          </div>
        </div>

        {binding.probing ? (
          <p className={styles.probing} role="status">
            <Spinner /> <span>{words.instance.running}</span>
          </p>
        ) : null}

        {binding.probeProblem === null ? null : (
          <Notice tone="caution" live>
            {binding.probeProblem}
          </Notice>
        )}

        {report === null ? null : (
          <div className={styles.probe}>
            <Notice tone={report.usable ? 'positive' : 'error'} live>
              {report.usable ? words.instance.usable : words.instance.unusable}
            </Notice>

            <ol className={styles.steps}>
              {probeStepRows(report).map((step) => (
                <li className={styles.step} key={step.id}>
                  <span className={styles.stepHead}>
                    <span className={OUTCOME_CLASS[step.outcome]} aria-hidden="true" />
                    <span className={styles.stepLabel}>{step.label}</span>
                    <span className={styles.stepStatus}>
                      {step.outcome === 'skipped'
                        ? words.instance.stepSkipped
                        : (step.status ? `HTTP ${step.status}` : words.instance.stepNoAnswer)}
                    </span>
                  </span>
                  {step.notes.length === 0 ? null : (
                    <ul className={styles.notes}>
                      {step.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>

            {report.usable ? (
              <dl className={styles.capabilities}>
                {capabilityRows(report).map((row) => (
                  <div className={styles.capability} key={row.id}>
                    <dt className={styles.capabilityLabel}>{row.label}</dt>
                    <dd className={styles.capabilityValue}>
                      <span className={STATE_CLASS[row.state]}>{row.value}</span>
                      {row.detail === null ? null : (
                        <span className={styles.capabilityDetail}>{row.detail}</span>
                      )}
                    </dd>
                  </div>
                ))}
                {report.capabilities.software === null ? null : (
                  <div className={styles.capability}>
                    <dt className={styles.capabilityLabel}>{words.instance.software}</dt>
                    <dd className={styles.capabilityValue}>
                      <span className={styles.stateServes}>
                        {report.capabilities.software.name} {report.capabilities.software.version}
                      </span>
                    </dd>
                  </div>
                )}
              </dl>
            ) : null}
          </div>
        )}

        <Notice tone="caution">{instanceSwitchWarning()}</Notice>

        {instance.source === 'saved-setting' ? (
          <div className={styles.resetRow}>
            <Button size="sm" onClick={binding.resetInstance}>
              {words.instance.useDefault}
            </Button>
          </div>
        ) : null}

        {/* The instance's own settings page, linked rather than rebuilt — it needs the instance's
            session, not Visious's token. Its sibling, the import/export link, lives on the Data tab. */}
        {preferencesLink === null ? null : (
          <div className={styles.externalLinks}>
            <p className={styles.externalLink}>
              <a href={preferencesLink.href} target="_blank" rel="noreferrer">
                {preferencesLink.label}
              </a>
              <span className={styles.rowDetail}>{preferencesLink.detail}</span>
            </p>
          </div>
        )}
      </Panel>

      <SyncSection binding={binding} />
    </>
  );
}

/**
 * Where the account's settings stand — a status, not a prompt, since the pull is automatic.
 *
 * The state is the *content* of this box, so it is read first and reads largest: the heading names
 * the question, then the reading (with its dot, and the spinner while a fetch is in flight), then
 * when it last happened, and only then the sentence explaining what that means. It used to be the
 * other way round, with the state hidden in a heading and the timestamp in muted caption type below
 * a paragraph.
 *
 * `role="status"` because nothing the reader pressed changes it: the sync module pulls on sign-in and
 * pushes on every write, so the line can change under a reader who is looking elsewhere on the page.
 */
function SyncSection({ binding }: { readonly binding: SettingsBinding }) {
  const { sync } = binding.state;
  const words = useCopy().text.settings;

  return (
    <Panel as="section" ariaLabel={words.syncState.title} className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.subTitle}>{words.syncState.title}</h2>
        
        <p className={styles.sectionLede}>{sync.copy.detail}</p>

        <p className={cx(styles.syncStatus, SYNC_TONE_CLASS[sync.tone])} role="status">
          <span className={styles.syncDot} aria-hidden="true" />
          <span>{sync.copy.status}</span>
          {sync.busy ? <Spinner small /> : null}
        </p>

        {sync.lastSyncedText === null ? null : (
          <p className={styles.syncStamp}>{words.account.lastSynced(sync.lastSyncedText)}</p>
        )}
      </div>

      {sync.problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{sync.problem.title}.</strong> {sync.problem.detail}
        </Notice>
      )}
    </Panel>
  );
}

/** Colour is a third channel behind the words and the dot, never the only one carrying the state. */
const SYNC_TONE_CLASS: Readonly<Record<SyncTone, string | undefined>> = {
  ok: styles.syncOk,
  pending: styles.syncPending,
  idle: styles.syncIdle,
};

/* ------------------------------------------------------------------------------------------------
 * Data — what this browser and the account hold, and how to drop it
 * ---------------------------------------------------------------------------------------------- */

/**
 * Two cards. The first is what accumulates: the two recording switches, with the instance's
 * import/export page linked under them (it operates on the same data, through the instance's own
 * session). The second is how to drop it — a line of clear buttons, every one behind the app's one
 * confirmation grammar (`ConfirmButton`): the first click arms, the second acts, and the armed title
 * says exactly what the second click destroys. Below the line, the four stores as a term-over-text
 * grid (the capabilities grid's grammar) — what lives in each with an example or two, what clearing
 * costs, and whether it comes back. The page's second deliberate exception to the row grammar.
 */
function DataTab({ binding }: { readonly binding: SettingsBinding }) {
  const data = binding.data;
  const settings = binding.state.settings;
  const copy = useCopy();
  const settingsWords = copy.text.settings;
  const control = settingsWords.control;
  const words = settingsWords.data;
  const confirm = copy.text.shell.confirm;
  const importExportLink = dataControlLink(binding.state.instance.url);

  return (
    <>
      <Panel as="section" ariaLabel={settingsWords.tab.data} className={styles.section}>
        {/* What gets recorded: the two switches that decide what accumulates. */}
        <div className={styles.rows}>
          <SettingRow
            title={control.mirrorHistory.label}
            storage="account"
            detail={control.mirrorHistory.detail}
            detailId={detailIdOf('settings-mirror-history')}
            detailTitle={control.mirrorHistory.detailTitle}
            control={
              <Toggle
                id="settings-mirror-history"
                label={control.mirrorHistory.label}
                hideLabel
                describedBy={detailIdOf('settings-mirror-history')}
                checked={settings.mirrorWatchHistory}
                onChange={binding.setMirrorWatchHistory}
              />
            }
          />
          <SettingRow
            title={control.resumePlayback.label}
            storage="account"
            detail={control.resumePlayback.detail}
            detailId={detailIdOf('settings-resume')}
            detailTitle={control.resumePlayback.detailTitle}
            control={
              <Toggle
                id="settings-resume"
                label={control.resumePlayback.label}
                hideLabel
                describedBy={detailIdOf('settings-resume')}
                checked={settings.resumePlayback}
                onChange={binding.setResumePlayback}
              />
            }
          />
        </div>

        {/* The instance's import/export tools, beside the switches that feed them the data. The
            link needs the instance's own session, not Visious's token. */}
        {importExportLink === null ? null : (
          <div className={styles.externalLinks}>
            <p className={styles.externalLink}>
              <a href={importExportLink.href} target="_blank" rel="noreferrer">
                {importExportLink.label}
              </a>
              <span className={styles.rowDetail}>{importExportLink.detail}</span>
            </p>
          </div>
        )}
      </Panel>

      <Panel as="section" ariaLabel={words.clearTitle} className={styles.section}>
        <div className={styles.cardHead}>
          <h2 className={styles.subTitle}>{words.clearTitle}</h2>
          <p className={styles.sectionLede}>{words.clearLede}</p>
        </div>

        {data.problem === null ? null : (
          <Notice tone="caution" live>
            <strong>{data.problem.title}.</strong> {data.problem.detail}
          </Notice>
        )}

        <div className={styles.actions}>
          <ConfirmButton
            confirmLabel={confirm}
            confirmTitle={words.history.confirmTitle}
            disabled={data.busy || (!data.signedIn && data.storedPositions === 0)}
            onConfirm={data.clearWatchHistory}
          >
            {words.history.label}
          </ConfirmButton>
          <ConfirmButton
            confirmLabel={confirm}
            confirmTitle={words.state.confirmTitle}
            onConfirm={data.clearLocalState}
          >
            {words.state.label}
          </ConfirmButton>
          <ConfirmButton
            confirmLabel={confirm}
            confirmTitle={words.cache.confirmTitle}
            onConfirm={data.clearLocalCache}
          >
            {words.cache.label}
          </ConfirmButton>
          {!data.signedIn ? null : (
            <ConfirmButton
              confirmLabel={confirm}
              confirmTitle={words.auth.confirmTitle}
              onConfirm={data.clearLocalAuth}
            >
              {words.auth.label}
            </ConfirmButton>
          )}
          <ConfirmButton
            confirmLabel={confirm}
            confirmTitle={words.all.confirmTitle}
            disabled={data.busy}
            onConfirm={data.clearAll}
          >
            {words.all.label}
          </ConfirmButton>
        </div>

        <dl className={styles.stores}>
          {[words.explain.history, words.explain.state, words.explain.cache, words.explain.auth].map(
            (explain) => (
              <div className={styles.store} key={explain.term}>
                <dt className={styles.storeTerm}>{explain.term}</dt>
                <dd className={styles.storeText}>{explain.text}</dd>
              </div>
            ),
          )}
        </dl>
      </Panel>
    </>
  );
}

const toOption = (language: { readonly tag: string; readonly label: string }) => ({
  id: language.tag,
  label: language.label,
});
